import { FORM_ERROR } from 'final-form';
import React from 'react';
import { deleteCompany } from '../../../../api/custom/DeleteCompany';
import { ADMIN_OVERVIEW } from '../../../../routes';
import { Card } from '../../../components/Cards';
import { Form } from '../../../components/Form';
import { FormErrorMessage } from '../../../components/Form/FormErrorMessage';
import { FormButtonContainer, FormRadioButton, FormRadioButtonContainer } from '../../../components/Form/FormInputs';
import { FormItem } from '../../../components/Form/FormItem';
import LinkButton from '../../../components/LinkButton';
import SaveButton, { DISABLED, ENABLED, SAVED, SAVING } from '../../../components/SaveButton';
import { interpolateAppUrl } from '../../../services/Url';
import { VALIDATORS } from '../../../services/Validation';
import styles from './style.m.less';
import type { DeleteRequest, Scope } from './types';

const OPTIONS: { [key in Scope]: { title: string; description: React.ReactNode } } = {
    CONTENT_ONLY_WITHOUT_CYLES: {
        title: 'Delete Content',
        description: (
            <>
                <strong>Start over without losing pre-configured cycles.</strong> By proceeding, you'll delete all
                content you and your staff have created within cycles, but the cycles themselves will remain. Only user
                accounts, cycles, and your company settings will remain.
            </>
        ),
    },
    CONTENT_ONLY_INCLUDING_CYCLES: {
        title: 'Delete All Content',
        description: (
            <>
                <strong>Start over with a clean slate.</strong> By proceeding, you'll delete all content you and your
                staff have created so far, including all cycles, reviews, 360 feedback and objectives. Only user
                accounts and your settings will remain.
            </>
        ),
    },
    ENTIRE_ACCOUNT: {
        title: 'Delete Entire Account & Data',
        description: (
            <>
                <strong>Delete all of this organization's data</strong> - including reviews, 360 feedback, objectives,
                user accounts and settings. No data associated with the company will remain.
            </>
        ),
    },
};

function getButtonState(submitting: boolean, submitSucceeded: boolean) {
    if (submitting) {
        return SAVING;
    }
    if (submitSucceeded) {
        return SAVED;
    }
    return ENABLED;
}

export default function DeleteCompanyForm() {
    const handleSubmit = (values: DeleteRequest) => {
        return deleteCompany(values)
            .then(() => {
                if (values.deletionScope === 'ENTIRE_ACCOUNT') {
                    location.href = '/loggedout';
                }
            })
            .catch(({ response }: any) => {
                let errorMessage = 'Deletion failed. Please try again or contact support.';
                if (response.status === 403) {
                    errorMessage = 'You must be a super admin to delete a company.';
                }
                if (response.status === 400) {
                    errorMessage = response.data.replace('deletionPassword', 'Deletion Password');
                }
                return { [FORM_ERROR]: errorMessage };
            });
    };

    return (
        <>
            <h2>What would you like to delete?</h2>
            <Card>
                <Form<DeleteRequest>
                    initialValues={{ deletionScope: 'CONTENT_ONLY_WITHOUT_CYLES' }}
                    onSubmit={handleSubmit}
                >
                    {({ submitting, submitSucceeded, submitError }) => (
                        <>
                            <FormRadioButtonContainer>
                                {Object.entries(OPTIONS).map(([scope, option]) => (
                                    <React.Fragment key={scope}>
                                        <FormItem
                                            value={scope}
                                            name="deletionScope"
                                            label={option.title}
                                            type="radio"
                                            inputComponent={FormRadioButton}
                                        />
                                        <div className={styles.description}>{option.description}</div>
                                    </React.Fragment>
                                ))}
                            </FormRadioButtonContainer>
                            <FormItem
                                name="deletionPassword"
                                label="Deletion Password"
                                type="password"
                                placeholder="Enter the deletion password"
                                autoComplete="off"
                                inputProps={{ autoFocus: true }}
                                validators={[VALIDATORS.required()]}
                            />
                            <FormButtonContainer>
                                <SaveButton
                                    kind="WARNING"
                                    labels={{
                                        [ENABLED]: 'Delete',
                                        [DISABLED]: 'Delete',
                                        [SAVING]: 'Deleting',
                                        [SAVED]: 'Deleted',
                                    }}
                                    state={getButtonState(submitting, submitSucceeded)}
                                />
                                <LinkButton href={interpolateAppUrl(ADMIN_OVERVIEW, {})} disabled={submitting}>
                                    Cancel
                                </LinkButton>
                                {submitError ? <FormErrorMessage errorMessage={submitError} /> : null}
                            </FormButtonContainer>
                        </>
                    )}
                </Form>
            </Card>
        </>
    );
}
